import { isEqual } from 'lodash';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAllLocations } from 'src/store/hooks/locations';
import { setRegionCookie } from 'src/utils/region';

import { usePrevious } from '@sravni/react-utils';
import { PAGE_TYPE } from '@sravni/ugc/lib/organizations/constants';

import { FILTER_NAMES, FILTERS } from '../../constants/reviews';
import { getFiltersFromQuery, ReviewType, TBackLink } from '../../helpers/reviews';
import { useTabsForSpecialOffers } from '../../hooks/useTabsForSpecialOffers';
import { useServerFilters } from '../../store/hooks/filters';
import { useOrganizationDetails, useOrganizations } from '../../store/hooks/organizations';
import { useProductsList } from '../../store/hooks/products';
import { useListQuery, useReviewsList } from '../../store/hooks/reviews';
import { useFingerprint } from '../../store/hooks/users';
import { fetchAllProducts, fetchProductsByTag } from '../../store/reducers/products';
import { fetchReviewsList } from '../../store/reducers/reviews';
import { TReviewListQuery } from '../../types/router';
import { createUrlWithFilters } from '../../utils/routing';
import { ReviewsListHeader } from '../ReviewsListHeader';

const SpecialOffersIdlePopup = dynamic(
  () => import('../SpecialOffersIdlePopup').then((mod) => mod.SpecialOffersIdlePopup),
  { ssr: false },
);
const ReviewsListContent = dynamic(() => import('./components/Content').then((mod) => mod.ReviewsListContent), {
  ssr: true,
});

export interface IReviewsListProps {
  reviewTypes: ReviewType;
  backLink: TBackLink;
}

export const ReviewsList: React.FC<IReviewsListProps> = ({ reviewTypes, backLink }) => {
  const dispatch = useDispatch();
  const organizations = useOrganizations();
  const serverFilters = useServerFilters();
  const products = useProductsList();
  const reviews = useReviewsList();
  const router = useRouter() as TRouter<TReviewListQuery>;
  const allLocations = useAllLocations();
  const listDefaultQuery = useListQuery();
  const { query } = router;
  const { organization, product } = query;
  const organizationDetails = useOrganizationDetails(organization);
  const { id: organizationId } = organizationDetails;
  const fp = useFingerprint();

  const currentServerFilters = serverFilters[reviewTypes.typeForService];
  const typePage = reviewTypes.itemTypeUrl;

  const isReviewsLoading = reviews.loading || reviews.isLoadingMore;
  const hasMore = reviews.total > reviews.items.length;

  const [page, setPage] = useState<number>(0);
  const prevPage = usePrevious(page);

  const [stateFilters, setFilters] = useState<FILTERS>({});

  const filters = useMemo(() => {
    return {
      ...listDefaultQuery,
      ...getFiltersFromQuery(query, { locations: allLocations }),
      ...(organizationId && { reviewObjectId: organizationId }),
      ...stateFilters,
    };
  }, [stateFilters, query, organizationId, listDefaultQuery, allLocations]);

  const productFromType = useMemo(
    () =>
      currentServerFilters?.tag?.find(
        (tag) =>
          !Object.values(PAGE_TYPE).includes(filters[FILTER_NAMES.TYPE]) && filters[FILTER_NAMES.TYPE] === tag.alias,
      )?.alias,
    [currentServerFilters?.tag, filters],
  );

  const filterForFetching = useMemo(
    () => ({
      ...filters,
      reviewObjectType: reviewTypes.reviewService,
      [FILTER_NAMES.FINGERPRINT]: fp,
      product: productFromType ?? filters.product,
    }),
    [filters, fp, productFromType, reviewTypes.reviewService],
  );

  const prevFilterForFetching = usePrevious(filterForFetching);

  const previousQuery = usePrevious(query);

  const specialOffersTabs = useTabsForSpecialOffers({
    reviewTag: filters[FILTER_NAMES.SERVICE] ?? filters[FILTER_NAMES.PRODUCT],
    type: query.type,
  });

  useEffect(() => {
    if (organization && product) {
      const productTag = currentServerFilters?.tag?.find((tag) => tag.alias === product);

      dispatch(
        fetchProductsByTag({
          tag: productTag?.key,
          tagAlias: productTag?.alias,
          type: reviewTypes.typeForService,
          id: filters.reviewObjectId.toString() || organizationId,
        }),
      );
    } else if (organization) {
      dispatch(
        fetchAllProducts({
          organizationId: filters.reviewObjectId || organizationId,
          type: reviewTypes.typeForService,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.reviewObjectId, organizationId, reviewTypes.typeForService]);

  useEffect(() => {
    if (previousQuery && previousQuery.organization !== query?.organization) setFilters({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const updateFilters = useCallback(
    async (value, target) => {
      const newFilters =
        typeof value === 'object' && !target // если это сразу пачка фильтров, например, из MobileFilters
          ? { ...filters, ...value }
          : {
              ...filters,
              [target]: value,
            };

      let link = null;

      if ((target === FILTER_NAMES.LOCATION_GAR_ID && value) || newFilters[FILTER_NAMES.LOCATION_GAR_ID]) {
        const newLocationGarId =
          target === FILTER_NAMES.LOCATION_GAR_ID ? value : newFilters[FILTER_NAMES.LOCATION_GAR_ID];

        const location = allLocations.find((el) => el.garId === newLocationGarId);
        location && setRegionCookie(location.routeGar);

        const filtersWithLocationAsAlias = {
          ...newFilters,
          ...(location && { location: location.alias }),
        };

        link = createUrlWithFilters(filtersWithLocationAsAlias, query);
      } else {
        link = createUrlWithFilters(newFilters, query);
      }

      await router.push(link.href, link.as, { scroll: false, shallow: true });

      setFilters(newFilters);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters, products, query, allLocations, productFromType],
  );

  const handleChange = useCallback(
    (value, target) => {
      updateFilters(value, target);
    },
    [updateFilters],
  );

  const handleDropFilters = useCallback(async () => {
    const newFilters = {
      ...filters,
      [FILTER_NAMES.FILTER]: listDefaultQuery[FILTER_NAMES.FILTER],
      [FILTER_NAMES.ORDER]: listDefaultQuery[FILTER_NAMES.ORDER],
      [FILTER_NAMES.LOCATION_GAR_ID]: listDefaultQuery[FILTER_NAMES.LOCATION_GAR_ID],
    };
    setFilters(newFilters);
    const link = createUrlWithFilters(newFilters, query);

    await router.push(link.href, link.as, { scroll: false, shallow: true });
  }, [filters, listDefaultQuery, query, router]);

  const isEqualFilters = isEqual(prevFilterForFetching, filterForFetching);

  useEffect(() => {
    if (isEqualFilters) return;
    setPage(0);
  }, [isEqualFilters]);

  useEffect(() => {
    if (!prevFilterForFetching || (isEqualFilters && page === prevPage)) return;

    dispatch(
      fetchReviewsList({
        filter: {
          ...filterForFetching,
          page,
        },
        organizations: organizations.organizationsList,
        organization: organizationDetails,
      }),
    );
  }, [
    dispatch,
    filterForFetching,
    isEqualFilters,
    organizations.organizationsList,
    organizationDetails,
    page,
    prevFilterForFetching,
    prevPage,
  ]);

  const handlePageScroll = useCallback(() => {
    if (!hasMore || isReviewsLoading) return;

    setPage((prev) => prev + 1);
  }, [hasMore, isReviewsLoading]);

  return (
    <>
      <ReviewsListHeader
        filters={filters}
        currentServerFilters={currentServerFilters}
        typePage={typePage}
        reviewTypes={reviewTypes}
        handleChange={handleChange}
        backLink={backLink}
      />
      <ReviewsListContent
        reviews={reviews}
        type={typePage}
        tags={currentServerFilters?.tag}
        reviewTypes={reviewTypes}
        handleDropFilters={handleDropFilters}
        handlePageScroll={handlePageScroll}
        specialOffersTabs={specialOffersTabs}
      />
      <SpecialOffersIdlePopup tabs={specialOffersTabs} />
    </>
  );
};
